import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { AccountType, loanStatusConstants } from 'config'
import { accountTypeWithBorrower } from 'config/user.type.constants'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { partiesMap } from 'reducers/loanDetail.reducer'
import { getAdminConfig, getRolodexCategories, setAdminConfig } from 'services'
import { ButtonGroup } from 'stories/components'
import { capitalizeFirstLetter, confirm, InputConvert, InputValidate, isEmpty, toCamelCase } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { deafultInputsV2 } from './constants'

const tabs: Record<string, Array<string>> = {
  views: [
    'pipelineSection',
    'nonServicingLoanStatus',
    'servicingLoanStatus',
    'borrowerLoanStatus',
    'pipelineTableSection',
    'overviewSection',
    'loanNumber',
    'entityName',
    'borrowerName',
    'propertyAddress',
    'loanProgram',
    'productSection',
    'productType',
    'productProgram',
    'productRate',
    'productPrice',
    'productRateDate',
    'productLockDays',
    'productRateSpread',
    'productExitFee',
    'partySection',
    'partyView',
    'pullThroughRatioSection',
    'pullThroughRatioView',
    'pullThroughRatioKeys',
  ],
  security: ['mfaEnabled'],
}

export const GlobalSettingsV2 = ({ tab }: { tab?: string }) => {
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])
  const [account, setAccount] = useState(AccountType.ADMIN)
  const [data, setData] = useState<Record<string, any>>({})

  const partyOptions: Record<string, any> = {
    accountExecutive: 'Account Executive [User]',
    broker: 'Broker [User]',
    creator: 'Creator [User]',
    loanOfficer: 'Loan Officer [User]',
    loanProcessor: 'Loan Processor [User]',
    underwriter: 'Underwriter [User]',
    accountManager: 'Account Manager [User]',
  }

  const [inputs, setInputs] = useState(deafultInputsV2(loanGlobalStatus, partyOptions))
  const [action, setAction] = useState('')

  let defaultNonServicingStatus: Record<string, boolean> = {},
    defaultServicingStatus: Record<string, boolean> = {}
  Object.keys(loanGlobalStatus).map((v: string) => {
    defaultNonServicingStatus[v] = false
  })
  Object.keys(loanStatusConstants.servicingStatus).map((v: string) => {
    defaultServicingStatus[v] = false
  })

  const initData = async () => {
    const [data, categories] = await Promise.all([getAdminConfig('globalSettingsV2'), getRolodexCategories()])

    const tempOptions = cloneDeep(partyOptions)
    Object.keys(categories)
      .sort((a, b) => (categories[a] > categories[b] ? 1 : -1))
      .map((key: any) => {
        tempOptions[capitalizeFirstLetter(toCamelCase(key))] = categories[key]
      })

    setData(data)
    const temp = cloneDeep(inputs)
    Object.keys(temp).forEach((key) => {
      temp[key].value = data[key]
      if (key === 'partyView') (temp[key] as any).options = tempOptions
    })
    setInputs(temp)
  }

  useEffect(() => {
    initData()
  }, [])

  const onChange = async (key: string, value: any) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    const isTurnOnMfa = key === 'mfaEnabled' && !newInputs[key].value && value
    if (isTurnOnMfa) {
      const isContinue = await confirm('Are you sure you want to turn on MFA for all users?')
      if (!isContinue) return
    }
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
    if (key === 'partyView') {
      const temp = cloneDeep(data)
      temp[key][account] = value
      setData(temp)
    }
    if (isTurnOnMfa) await onSubmit(newInputs)

    return newInputs
  }

  const onSubmit = async (_newInputs?: Record<string, any>) => {
    let hasError = false
    let newInputs = _newInputs || cloneDeep(inputs)

    const rtl: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      if (key === 'partyView') {
        rtl[key] = data[key]
        rtl[key][account] = newInputs[key].value
      } else rtl[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    rtl.pipeline = data.pipeline
    setInputs(newInputs)
    if (hasError) return
    setAction('setting')
    await setAdminConfig('globalSettingsV2', rtl)
    setAction('')
  }

  const renderPullThroughRatioKeys = () => {
    const pullThroughRatioView = inputs.pullThroughRatioView.value
    let pullThroughRatioKeys = inputs.pullThroughRatioKeys.value
    if (!pullThroughRatioKeys || typeof pullThroughRatioKeys != 'object') pullThroughRatioKeys = {}

    const onChangeValue = async (key: string, value: string) => {
      pullThroughRatioKeys[key] = value
      const newInputs = await onChange('pullThroughRatioKeys', pullThroughRatioKeys)
      await onSubmit(newInputs)
    }

    return (
      <div className="grid grid-cols-3 gap-4">
        {pullThroughRatioView.map((key: any) => (
          <div>
            <RenderInput
              input={{
                inputType: 'select',
                options: {
                  companyName: 'Company Name',
                  name: 'Name',
                },
                value: pullThroughRatioKeys[key],
                defaultSelected: false,
                title: partiesMap[key],
              }}
              Key={key}
              onChange={onChangeValue}
            />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="relative mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <LayoutLoading show={!isEmpty(action)} />
      {Object.keys(data).length
        ? Object.keys(inputs).map((key, index) => {
            if (tab && !tabs[tab].includes(key)) return
            let input = inputs[key]
            if (['nonServicingLoanStatus', 'borrowerLoanStatus', 'activeLoanStatusForAEDisable'].includes(key))
              input.value = { ...defaultNonServicingStatus, ...input.value }
            if (key == 'servicingLoanStatus') input.value = { ...defaultServicingStatus, ...input.value }
            if (key === 'partyView') input.value = data.partyView[account]
            if (key == 'pullThroughRatioKeys') (input as any).render = renderPullThroughRatioKeys

            const rtl = (
              <>
                <div className={`input md:col-span-${input.span || 1} `} key={index}>
                  <RenderInput input={input} Key={key} onChange={onChange} onBlur={() => onSubmit()} />
                </div>
                {key === 'partySection' && (
                  <div className="col-span-full" key={`party-${index}`}>
                    <ButtonGroup title={accountTypeWithBorrower()} value={account} onChange={setAccount} sort />
                  </div>
                )}
              </>
            )
            return rtl
          })
        : null}
    </div>
  )
}
